import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Check, X, Minus, AlertCircle, Loader2 } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_poll-engine-1/artifacts/tvzc3ev3_TMC-Coms-Logo.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResponsePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [status, setStatus] = useState("loading"); // loading, confirming, success, error, already_voted
  const [pollData, setPollData] = useState(null);
  const [nonce, setNonce] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const hasAutoSubmitted = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMessage("Invalid or missing token");
      return;
    }

    // Extract option from token (it's encoded in the signed token)
    initVote();
  }, [token]);

  const initVote = async () => {
    try {
      // Decode token to get option (base64 -> poll:recipient:option:sig)
      let decodedOption = "yes"; // default
      try {
        const decoded = atob(token);
        const parts = decoded.split(":");
        if (parts.length >= 3) {
          decodedOption = parts[2];
        }
      } catch (e) {
        console.error("Failed to decode token");
      }

      setSelectedOption(decodedOption);

      const response = await fetch(`${API}/vote/init`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, option: decodedOption })
      });

      const data = await response.json();

      if (response.ok) {
        setPollData(data);
        setNonce(data.nonce);
        
        // Check if already voted
        if (data.existing_response) {
          setStatus("already_voted");
        } else {
          setStatus("confirming");
          // Auto-submit after a brief moment (anti-bot: requires JS to finalize)
          if (!hasAutoSubmitted.current) {
            hasAutoSubmitted.current = true;
            setTimeout(() => {
              finalizeVote(data.nonce, decodedOption);
            }, 500);
          }
        }
      } else {
        setStatus("error");
        setErrorMessage(data.detail || "Failed to process your response");
      }
    } catch (error) {
      setStatus("error");
      setErrorMessage("Connection error. Please try again.");
    }
  };

  const finalizeVote = async (nonceValue, option) => {
    try {
      const response = await fetch(`${API}/vote/finalize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          token, 
          option, 
          nonce: nonceValue 
        })
      });

      const data = await response.json();

      if (response.ok) {
        setStatus("success");
      } else {
        setStatus("error");
        setErrorMessage(data.detail || "Failed to record your response");
      }
    } catch (error) {
      setStatus("error");
      setErrorMessage("Connection error. Please try again.");
    }
  };

  const handleManualSubmit = () => {
    if (nonce && selectedOption) {
      setStatus("confirming");
      finalizeVote(nonce, selectedOption);
    }
  };

  const handleChangeVote = async (newOption) => {
    setSelectedOption(newOption);
    setStatus("confirming");
    
    // Re-init with new option
    try {
      const response = await fetch(`${API}/vote/init`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, option: newOption })
      });

      const data = await response.json();

      if (response.ok) {
        setNonce(data.nonce);
        finalizeVote(data.nonce, newOption);
      } else {
        setStatus("error");
        setErrorMessage(data.detail || "Failed to change your response");
      }
    } catch (error) {
      setStatus("error");
      setErrorMessage("Connection error. Please try again.");
    }
  };

  const getOptionIcon = (option) => {
    if (option === "yes") return <Check className="h-6 w-6" strokeWidth={2} />;
    if (option === "no") return <X className="h-6 w-6" strokeWidth={2} />;
    return <Minus className="h-6 w-6" strokeWidth={2} />;
  };

  const getOptionLabel = (option) => {
    if (option === "yes") return "Yes";
    if (option === "no") return "No";
    return "Don't Care";
  };

  const getOptionColor = (option) => {
    if (option === "yes") return "text-green-500";
    if (option === "no") return "text-red-500";
    return "text-slate-400";
  };

  return (
    <div className="response-container bg-background">
      <div className="response-card animate-fade-in">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img 
            src={LOGO_URL} 
            alt="TMC Communication" 
            className="h-14 w-auto"
            data-testid="response-logo"
          />
        </div>

        <Card className="bg-card border-border">
          <CardContent className="p-8">
            {/* Loading State */}
            {status === "loading" && (
              <div className="text-center py-8">
                <Loader2 className="h-12 w-12 mx-auto mb-4 text-primary animate-spin" strokeWidth={1.5} />
                <p className="text-muted-foreground">Loading poll...</p>
              </div>
            )}

            {/* Confirming State */}
            {status === "confirming" && (
              <div className="text-center py-8">
                <Loader2 className="h-12 w-12 mx-auto mb-4 text-primary animate-spin" strokeWidth={1.5} />
                <p className="text-lg font-medium mb-2">Recording your response...</p>
                <p className="text-muted-foreground">
                  You selected: <span className={`font-semibold ${getOptionColor(selectedOption)}`}>
                    {getOptionLabel(selectedOption)}
                  </span>
                </p>
                
                {/* Fallback button for no-JS */}
                <noscript>
                  <div className="mt-6">
                    <Button 
                      onClick={handleManualSubmit}
                      className="btn-glow"
                    >
                      Click to Confirm
                    </Button>
                  </div>
                </noscript>
              </div>
            )}

            {/* Success State */}
            {status === "success" && (
              <div className="text-center py-8" data-testid="success-state">
                <div className={`h-16 w-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                  selectedOption === "yes" ? "bg-green-500/20 text-green-500" :
                  selectedOption === "no" ? "bg-red-500/20 text-red-500" :
                  "bg-slate-500/20 text-slate-400"
                }`}>
                  {getOptionIcon(selectedOption)}
                </div>
                <h2 className="text-2xl font-bold mb-2">Response Recorded</h2>
                <p className="text-muted-foreground mb-4">
                  Your response: <span className={`font-semibold ${getOptionColor(selectedOption)}`}>
                    {getOptionLabel(selectedOption)}
                  </span>
                </p>
                
                {pollData && (
                  <div className="mt-6 p-4 bg-secondary/50 rounded-lg text-left">
                    <p className="text-sm text-muted-foreground mb-1">Poll:</p>
                    <p className="font-medium">{pollData.poll_title}</p>
                  </div>
                )}

                <p className="text-sm text-muted-foreground mt-6">
                  Thank you for your response!
                </p>
              </div>
            )}

            {/* Already Voted State - Only for non-anonymous polls */}
            {status === "already_voted" && pollData && !pollData.anonymous && (
              <div className="text-center py-6" data-testid="already-voted-state">
                <div className={`h-16 w-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                  pollData.existing_response === "yes" ? "bg-green-500/20 text-green-500" :
                  pollData.existing_response === "no" ? "bg-red-500/20 text-red-500" :
                  "bg-slate-500/20 text-slate-400"
                }`}>
                  {getOptionIcon(pollData.existing_response)}
                </div>
                <h2 className="text-xl font-bold mb-2">You've Already Responded</h2>
                <p className="text-muted-foreground mb-2">
                  Your current response: <span className={`font-semibold ${getOptionColor(pollData.existing_response)}`}>
                    {getOptionLabel(pollData.existing_response)}
                  </span>
                </p>

                <div className="mt-6 p-4 bg-secondary/50 rounded-lg text-left mb-6">
                  <p className="text-sm text-muted-foreground mb-1">Poll:</p>
                  <p className="font-medium">{pollData.poll_title}</p>
                  <p className="text-sm text-muted-foreground mt-2">{pollData.poll_question}</p>
                </div>

                <p className="text-sm text-muted-foreground mb-4">
                  Want to change your response?
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={() => handleChangeVote("yes")}
                    className={`btn-yes active-scale text-white ${pollData.existing_response === "yes" ? "ring-2 ring-green-500 ring-offset-2 ring-offset-card" : ""}`}
                    data-testid="change-vote-yes"
                  >
                    <Check className="h-4 w-4 mr-2" /> Yes
                  </Button>
                  <Button
                    onClick={() => handleChangeVote("no")}
                    className={`btn-no active-scale text-white ${pollData.existing_response === "no" ? "ring-2 ring-red-500 ring-offset-2 ring-offset-card" : ""}`}
                    data-testid="change-vote-no"
                  >
                    <X className="h-4 w-4 mr-2" /> No
                  </Button>
                  <Button
                    onClick={() => handleChangeVote("neutral")}
                    className={`btn-neutral active-scale text-white ${pollData.existing_response === "neutral" ? "ring-2 ring-slate-500 ring-offset-2 ring-offset-card" : ""}`}
                    data-testid="change-vote-neutral"
                  >
                    <Minus className="h-4 w-4 mr-2" /> Don't Care
                  </Button>
                </div>
              </div>
            )}

            {/* Error State */}
            {status === "error" && (
              <div className="text-center py-8" data-testid="error-state">
                <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                  <AlertCircle className="h-8 w-8 text-red-500" strokeWidth={1.5} />
                </div>
                <h2 className="text-xl font-bold mb-2 text-red-500">Unable to Process</h2>
                <p className="text-muted-foreground">{errorMessage}</p>
                <p className="text-sm text-muted-foreground mt-4">
                  Please contact your administrator if this problem persists.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          TMC Communication
        </p>
      </div>
    </div>
  );
}
